import { useTranslation } from 'react-i18next'
import type { CompletionType } from '../../../types/custody-actions'
import { EvidenceItemSelector } from './EvidenceItemSelector'

export interface ReceiveFormState {
  selectedIds: string[]
  completionType: CompletionType
  actionDate: string
  actionTime: string
  fromName: string
  fromRole: string
  fromOrg: string
  toName: string
  toRole: string
  toOrg: string
  method: string
  conditionNotes: string
  notes: string
}

export type Updater = <K extends keyof ReceiveFormState>(key: K, value: ReceiveFormState[K]) => void

interface ReceiveSelectorSectionProps {
  caseId: string
  state: ReceiveFormState
  update: Updater
  linkageResolved: boolean
}

export function ReceiveSelectorSection({
  caseId,
  state,
  update,
  linkageResolved,
}: ReceiveSelectorSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <EvidenceItemSelector
        caseId={caseId}
        selectedIds={state.selectedIds}
        onChange={(ids) => {
          update('selectedIds', ids)
        }}
      />
      {linkageResolved && (
        <div
          role="status"
          className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"
        >
          {t('custodyActions.linkageCheck.linkageResolved')}
        </div>
      )}
    </>
  )
}

interface ReceiveDateTimeSectionProps {
  state: ReceiveFormState
  update: Updater
}

export function ReceiveDateTimeSection({
  state,
  update,
}: ReceiveDateTimeSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div className="flex gap-4">
        <div className="flex-1">
          <label htmlFor="recv-date" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.actionDate')}
          </label>
          <input
            id="recv-date"
            type="date"
            value={state.actionDate}
            onChange={(ev) => {
              update('actionDate', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="recv-time" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.actionTime')}
          </label>
          <input
            id="recv-time"
            type="time"
            value={state.actionTime}
            onChange={(ev) => {
              update('actionTime', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      <div>
        <p className="mb-1 text-xs text-[var(--text-muted)]">
          {t('custodyActions.completionType')}
        </p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="recv-completion"
              value="complete"
              checked={state.completionType === 'complete'}
              onChange={() => {
                update('completionType', 'complete')
              }}
            />
            {t('custodyActions.complete')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="recv-completion"
              value="partial"
              checked={state.completionType === 'partial'}
              onChange={() => {
                update('completionType', 'partial')
              }}
            />
            {t('custodyActions.partial')}
          </label>
        </div>
      </div>
    </>
  )
}

interface ReceiveNotesSectionProps {
  state: ReceiveFormState
  update: Updater
  error: string | null
}

export function ReceiveNotesSection({
  state,
  update,
  error,
}: ReceiveNotesSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div>
        <label htmlFor="recv-method" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.method')}
        </label>
        <input
          id="recv-method"
          type="text"
          value={state.method}
          onChange={(ev) => {
            update('method', ev.target.value)
          }}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label htmlFor="recv-condition" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.conditionNotes')}
        </label>
        <textarea
          id="recv-condition"
          value={state.conditionNotes}
          onChange={(ev) => {
            update('conditionNotes', ev.target.value)
          }}
          rows={2}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label htmlFor="recv-notes" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.notes')}
        </label>
        <textarea
          id="recv-notes"
          value={state.notes}
          onChange={(ev) => {
            update('notes', ev.target.value)
          }}
          rows={2}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      {error && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
    </>
  )
}
